import axios from "axios";
import { getToken, getUser } from "../Utils/Common";
import token_valid from "./TokenValid";

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || "http://localhost:8000",
  timeout: 30000, // 30 seconds global timeout
  headers: {
    "Content-Type": "application/json",
  },
});

/**
 * Get export product prices with pagination and filtering
 * Returns list with current retail/wholesale prices, cost, and profit
 */
const getExportProductPrices = async (offset, pageSize, filter) => {
  let token_valid_result = await token_valid();
  if (token_valid_result) {
    let token = getToken();
    return axios
      .post(
        process.env.REACT_APP_API_URL +
          "/api/export/product-price/list/" +
          offset +
          "/" +
          pageSize,
        {
          barcode: filter._barcode ? filter._barcode.trim() : null,
          productName: filter._product ? filter._product.trim() : null,
          priceType: filter._priceType ? filter._priceType.trim() : null,
        },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + token,
          },
        },
      )
      .then((response) => {
        console.log("Export Product Prices Response:", response.data);
        return response.data;
      })
      .catch((error) => {
        console.log("Get export product prices error:", error);
        return "Something went wrong...";
      });
  }
};

/**
 * Get current pricing for a specific product by BARCODE
 * Returns: retail price, wholesale price, and current cost
 */
const getCurrentProductPricing = async (barcode) => {
  let token_valid_result = await token_valid();
  if (token_valid_result) {
    let token = getToken();
    return axios
      .get(
        process.env.REACT_APP_API_URL +
          "/api/export/product-price/" +
          barcode +
          "/current",
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + token,
          },
        },
      )
      .then((response) => {
        console.log("Current Product Pricing:", response.data);
        return response.data;
      })
      .catch((error) => {
        console.log("Get current product pricing error:", error);
        return "Something went wrong...";
      });
  }
};

/**
 * Get current cost for a specific product by BARCODE
 */
const getCurrentProductCost = async (barcode) => {
  let token_valid_result = await token_valid();
  if (token_valid_result) {
    let token = getToken();
    return axios
      .get(
        process.env.REACT_APP_API_URL +
          "/api/export/product-price/" +
          barcode +
          "/cost",
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + token,
          },
        },
      )
      .then((response) => {
        console.log("Current Product Cost:", response.data);
        return response.data;
      })
      .catch((error) => {
        console.log("Get current product cost error:", error);
        return "Something went wrong...";
      });
  }
};

/**
 * Update product cost by BARCODE
 * Body: { materialCost, electricityCost, deliveryCharges, employeeCost, externalCost }
 */
const updateProductCost = async (barcode, costData) => {
  let token_valid_result = await token_valid();
  if (token_valid_result) {
    let token = getToken();
    return axios
      .put(
        process.env.REACT_APP_API_URL +
          "/api/export/product-price/" +
          barcode +
          "/cost",
        {
          materialCost: costData.materialCost || 0,
          electricityCost: costData.electricityCost || 0,
          deliveryCharges: costData.deliveryCharges || 0,
          employeeCost: costData.employeeCost || 0,
          externalCost: costData.externalCost || 0,
        },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + token,
          },
        },
      )
      .then((response) => {
        console.log("Update Product Cost Response:", response.data);
        return true;
      })
      .catch((error) => {
        console.log("Update product cost error:", error);
        return false;
      });
  }
};

/**
 * Assign a price (retail or wholesale) to a product by BARCODE
 * Body: { priceType: "RETAIL" | "WHOLESALE", sellingPrice, assignedBy, note }
 */
const assignProductPrice = async (barcode, priceData) => {
  let token_valid_result = await token_valid();
  if (token_valid_result) {
    let token = getToken();
    let user = getUser();

    return axios
      .post(
        process.env.REACT_APP_API_URL +
          "/api/export/product-price/" +
          barcode +
          "/assign-price",
        {
          priceType: priceData.priceType, // "RETAIL" or "WHOLESALE"
          sellingPrice: priceData.sellingPrice,
          assignedBy: user.username,
          note: priceData.note || "",
        },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + token,
          },
        },
      )
      .then((response) => {
        console.log("Assign Price Response:", response.data);
        return true;
      })
      .catch((error) => {
        console.log("Assign product price error:", error);
        return false;
      });
  }
};

/**
 * Get price history for a product by BARCODE
 * Optional priceType filter: "RETAIL" or "WHOLESALE"
 */
const getProductPriceHistory = async (barcode, priceType = null) => {
  let token_valid_result = await token_valid();
  if (token_valid_result) {
    let token = getToken();
    let url =
      process.env.REACT_APP_API_URL +
      "/api/export/product-price/" +
      barcode +
      "/history";

    if (priceType) {
      url += "?priceType=" + priceType;
    }

    return axios
      .get(url, {
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + token,
        },
      })
      .then((response) => {
        console.log("Product Price History:", response.data);
        return response.data;
      })
      .catch((error) => {
        console.log("Get product price history error:", error);
        return "Something went wrong...";
      });
  }
};

/**
 * Trigger 6-month price record cleanup (admin only)
 */
const cleanupOldPriceRecords = async () => {
  let token_valid_result = await token_valid();
  if (token_valid_result) {
    let token = getToken();
    return axios
      .delete(
        process.env.REACT_APP_API_URL + "/api/export/product-price/cleanup",
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + token,
          },
        },
      )
      .then((response) => {
        console.log("Cleanup Response:", response.data);
        return response.data.deletedCount;
      })
      .catch((error) => {
        console.log("Cleanup old price records error:", error);
        return 0;
      });
  }
};

/**
 * Get current material cost for a specific product by BARCODE id
 */
const getCurrentMaterialCost = async (barcodeId) => {
  let token_valid_result = await token_valid();
  if (token_valid_result) {
    let token = getToken();
    return axios
      .get(
        process.env.REACT_APP_API_URL +
          "/api/export/product-price/material-cost/" +
          barcodeId +
          "/total",
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + token,
          },
        },
      )
      .then((response) => {
        console.log("Current Product Material Cost:", response.data);
        return response.data;
      })
      .catch((error) => {
        console.log("Get current product material cost error:", error);
        return "Something went wrong...";
      });
  }
};

export {
  getExportProductPrices,
  getCurrentProductPricing,
  getCurrentProductCost,
  updateProductCost,
  assignProductPrice,
  getProductPriceHistory,
  cleanupOldPriceRecords,
  getCurrentMaterialCost,
};
